import React, { useState, useEffect, useRef, useMemo } from 'react';
import { X, Save, RefreshCw, Globe } from 'lucide-react';
import { Switch } from '../ui/switch';
import { Button } from '../ui/button';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from '../ui/alert-dialog';
import { api } from '../../lib/api';
import { toast } from 'sonner';

const TABS = ['Diseño', 'Campos', 'Cupón', 'Disparadores', 'Segmentación', 'Traducciones', 'Integración'];

const LANGUAGE_NAMES = { en: 'Inglés', fr: 'Francés', it: 'Italiano', de: 'Alemán', pt: 'Portugués', es: 'Español', nl: 'Holandés', ca: 'Catalán' };
const FALLBACK_LANGUAGES = ['en', 'fr', 'it', 'de', 'pt'];

const FIELD_DEFS = [
  { key: 'email', label: 'Email', fixed: true },
  { key: 'phone', label: 'Teléfono' },
  { key: 'name', label: 'Nombre' },
  { key: 'last_name', label: 'Apellidos' },
  { key: 'consent', label: 'Consentimiento (RGPD)' },
];

function fieldsToMap(fieldsConfig) {
  const map = {};
  FIELD_DEFS.forEach(f => { map[f.key] = { included: f.fixed || false, required: f.fixed || false, label: f.label, policy_url: '' }; });
  (fieldsConfig || []).forEach(f => { map[f.key] = { included: true, required: !!f.required, label: f.label || map[f.key]?.label, policy_url: f.policy_url || '' }; });
  return map;
}

function mapToFields(map) {
  return FIELD_DEFS.filter(f => map[f.key]?.included).map(f => ({
    key: f.key, label: map[f.key].label || f.label, required: !!map[f.key].required,
    ...(f.key === 'consent' ? { policy_url: (map[f.key].policy_url || '').trim() } : {}),
  }));
}

const Section = ({ title, children }) => (
  <div className="bg-gray-950/50 border border-gray-800 rounded-xl p-4 mb-3">
    <h5 className="text-gray-300 text-xs font-medium mb-3">{title}</h5>
    {children}
  </div>
);

const Input = (props) => (
  <input {...props}
    className={`w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 ${props.className || ''}`}
  />
);

export const PopupEditorModal = ({ campaign, integrations, onClose, onSaved }) => {
  const [tab, setTab] = useState('Diseño');
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState(campaign.name || '');
  const [design, setDesign] = useState(campaign.design || {});
  const [fieldsMap, setFieldsMap] = useState(fieldsToMap(campaign.fields_config));
  const [triggers, setTriggers] = useState(campaign.triggers || {});
  const [targeting, setTargeting] = useState(campaign.targeting || { customer_type: 'all' });
  const [frequency, setFrequency] = useState(campaign.frequency || { dont_show_days: 7 });
  const [integration, setIntegration] = useState(campaign.integration || { provider: 'none' });
  const [coupon, setCoupon] = useState(campaign.coupon || { mode: 'static' });
  const [translations, setTranslations] = useState(campaign.translations || {});
  const [detectedLanguages, setDetectedLanguages] = useState([]);
  const [transLang, setTransLang] = useState(null);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  const updDesign = (k, v) => setDesign(prev => ({ ...prev, [k]: v }));
  const updTrigger = (k, sub, v) => setTriggers(prev => ({ ...prev, [k]: { ...prev[k], [sub]: v } }));
  const updField = (k, sub, v) => setFieldsMap(prev => ({ ...prev, [k]: { ...prev[k], [sub]: v } }));
  const updCoupon = (k, v) => setCoupon(prev => ({ ...prev, [k]: v }));
  const updTranslation = (lang, key, value) => setTranslations(prev => ({ ...prev, [lang]: { ...prev[lang], [key]: value } }));
  const updTranslationField = (lang, fieldKey, sub, value) => setTranslations(prev => ({
    ...prev,
    [lang]: {
      ...prev[lang],
      fields: { ...(prev[lang]?.fields || {}), [fieldKey]: { ...(prev[lang]?.fields?.[fieldKey] || {}), [sub]: value } },
    },
  }));

  // Idiomas disponibles: los detectados en el conector PrestaShop (multistore/multilang)
  // de la organización, o una lista genérica de respaldo si no hay conector configurado.
  useEffect(() => {
    api.get('/api/connectors/prestashop/status').then(r => {
      const langs = (r.data?.detected_languages || []).filter(l => l !== 'es');
      setDetectedLanguages(langs.length ? langs : FALLBACK_LANGUAGES);
      setTransLang(prev => prev || (langs.length ? langs[0] : FALLBACK_LANGUAGES[0]));
    }).catch(() => {
      setDetectedLanguages(FALLBACK_LANGUAGES);
      setTransLang(prev => prev || FALLBACK_LANGUAGES[0]);
    });
  }, []);

  // ── Confirmación antes de cerrar si hay cambios sin guardar ──
  const initialSnapshotRef = useRef(JSON.stringify({
    name: campaign.name || '', design: campaign.design || {}, fieldsMap: fieldsToMap(campaign.fields_config),
    triggers: campaign.triggers || {}, targeting: campaign.targeting || { customer_type: 'all' },
    frequency: campaign.frequency || { dont_show_days: 7 }, integration: campaign.integration || { provider: 'none' },
    coupon: campaign.coupon || { mode: 'static' }, translations: campaign.translations || {},
  }));
  const isDirty = useMemo(() => {
    const current = JSON.stringify({ name, design, fieldsMap, triggers, targeting, frequency, integration, coupon, translations });
    return current !== initialSnapshotRef.current;
  }, [name, design, fieldsMap, triggers, targeting, frequency, integration, coupon, translations]);

  const attemptClose = () => {
    if (isDirty) setShowDiscardConfirm(true);
    else onClose();
  };

  const save = async (statusOverride) => {
    setSaving(true);
    try {
      const payload = {
        name, design,
        fields_config: mapToFields(fieldsMap),
        triggers, targeting, frequency, integration, coupon, translations,
      };
      if (statusOverride) payload.status = statusOverride;
      await api.put(`/api/popups/${campaign.campaign_id}`, payload);
      toast.success('Popup guardado');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const providerLists = integration.provider !== 'none' ? (integrations?.[integration.provider]?.lists || []) : [];

  return (
    <>
    <div
      data-testid="popup-editor-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={attemptClose}
    >
      <div
        className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-2xl max-h-[88vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-6 pb-4 sticky top-0 bg-gray-900 border-b border-gray-800 z-10">
          <Input value={name} onChange={e => setName(e.target.value)} data-testid="popup-name-input"
            className="text-white font-semibold text-base bg-transparent border-none px-0 focus:border-none" />
          <button onClick={attemptClose} className="text-gray-500 hover:text-white ml-3" data-testid="popup-editor-close-btn">
            <X size={18} />
          </button>
        </div>

        <div className="flex gap-1 px-6 pt-3 border-b border-gray-800 overflow-x-auto">
          {TABS.map(t => (
            <button key={t} onClick={() => setTab(t)} data-testid={`popup-tab-${t.toLowerCase()}`}
              className={`px-3 py-2 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${
                tab === t ? 'text-indigo-400 border-indigo-500' : 'text-gray-500 border-transparent hover:text-gray-300'
              }`}>
              {t}
            </button>
          ))}
        </div>

        <div className="p-6">
          {tab === 'Diseño' && (
            <>
              <Section title="Contenido">
                <div className="space-y-2">
                  <Input placeholder="Titular" value={design.headline || ''} onChange={e => updDesign('headline', e.target.value)} data-testid="design-headline-input" />
                  <textarea placeholder="Texto" value={design.body_text || ''} onChange={e => updDesign('body_text', e.target.value)}
                    data-testid="design-body-input" rows={2}
                    className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500" />
                  <Input placeholder="Texto del botón" value={design.button_text || ''} onChange={e => updDesign('button_text', e.target.value)} data-testid="design-button-text-input" />
                  <Input placeholder="URL de imagen (opcional)" value={design.image_url || ''} onChange={e => updDesign('image_url', e.target.value)} data-testid="design-image-url-input" />
                </div>
              </Section>
              <Section title="Colores">
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center gap-2">
                    <input type="color" value={design.primary_color || '#6366f1'} onChange={e => updDesign('primary_color', e.target.value)} data-testid="design-primary-color-input" className="w-9 h-9 rounded bg-transparent border border-gray-700" />
                    <span className="text-gray-400 text-xs">Color principal</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="color" value={design.background_color || '#ffffff'} onChange={e => updDesign('background_color', e.target.value)} data-testid="design-bg-color-input" className="w-9 h-9 rounded bg-transparent border border-gray-700" />
                    <span className="text-gray-400 text-xs">Fondo</span>
                  </div>
                </div>
              </Section>
              <Section title="Mensaje de éxito (tras registrarse)">
                <div className="space-y-2">
                  <textarea
                    placeholder={integration.enable_double_optin
                      ? '¡Revisa tu email! Te hemos enviado un enlace para confirmar tu suscripción.'
                      : 'Te hemos suscrito correctamente.'}
                    value={design.success_message || ''}
                    onChange={e => updDesign('success_message', e.target.value)}
                    data-testid="design-success-message-input" rows={2}
                    className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500" />
                  <p className="text-gray-600 text-[11px]">Si lo dejas vacío, se usará un mensaje por defecto (cambia automáticamente si activas el Doble Opt-in en la pestaña Integración).</p>
                  <div className="flex items-center justify-between pt-1">
                    <div>
                      <span className="text-white text-sm">Mostrar código de descuento en el popup</span>
                      <p className="text-gray-600 text-[11px]">Si lo desactivas, el código no aparecerá aquí; deberá enviarse por email (Klaviyo/Brevo).</p>
                    </div>
                    <Switch checked={design.show_discount_on_success !== false}
                      onCheckedChange={v => updDesign('show_discount_on_success', v)} data-testid="design-show-discount-switch" />
                  </div>
                </div>
              </Section>
            </>
          )}

          {tab === 'Cupón' && (
            <>
              <Section title="Tipo de cupón">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <span className="text-white text-sm">Código único por registro</span>
                    <p className="text-gray-600 text-[11px]">En vez de un código fijo compartido, cada lead recibe su propio código, creado automáticamente en tu tienda (PrestaShop/Shopify) con la conexión que ya tienes en Conectores.</p>
                  </div>
                  <Switch checked={coupon.mode === 'dynamic'}
                    onCheckedChange={v => updCoupon('mode', v ? 'dynamic' : 'static')} data-testid="coupon-mode-switch" />
                </div>
                {coupon.mode !== 'dynamic' && (
                  <div className="pt-2 border-t border-gray-800">
                    <label className="text-gray-500 text-[11px] block mb-1">Código de descuento (opcional)</label>
                    <Input placeholder="Ej: BIENVENIDO10" value={design.discount_code || ''}
                      onChange={e => updDesign('discount_code', e.target.value)} data-testid="design-discount-input" />
                    <p className="text-gray-600 text-[11px] mt-1">Este código es fijo y se muestra en el <strong>mensaje de gracias</strong> tras el registro (pestaña Diseño → "Mensaje de éxito"), nunca antes.</p>
                  </div>
                )}
                {coupon.mode === 'dynamic' && (
                  <div className="space-y-2 pt-2 border-t border-gray-800">
                    <div className="grid grid-cols-2 gap-2">
                      <select value={coupon.discount_type || 'percentage'}
                        onChange={e => updCoupon('discount_type', e.target.value)}
                        data-testid="coupon-discount-type-select"
                        className="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white">
                        <option value="percentage">% Porcentaje</option>
                        <option value="fixed">Importe fijo (€)</option>
                      </select>
                      <Input type="number" min="0" placeholder={coupon.discount_type === 'fixed' ? 'Importe (€)' : 'Porcentaje (%)'}
                        value={coupon.value ?? ''} onChange={e => updCoupon('value', e.target.value === '' ? '' : Number(e.target.value))}
                        data-testid="coupon-value-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-gray-500 text-[11px] block mb-1">Caduca a los (días)</label>
                        <Input type="number" min="1" placeholder="7" value={coupon.expiration_days ?? ''}
                          onChange={e => updCoupon('expiration_days', e.target.value === '' ? '' : Number(e.target.value))}
                          data-testid="coupon-expiration-input" />
                      </div>
                      <div>
                        <label className="text-gray-500 text-[11px] block mb-1">Compra mínima (opcional)</label>
                        <Input type="number" min="0" placeholder="0" value={coupon.min_purchase_amount ?? ''}
                          onChange={e => updCoupon('min_purchase_amount', e.target.value === '' ? '' : Number(e.target.value))}
                          data-testid="coupon-min-purchase-input" />
                      </div>
                    </div>
                    <div>
                      <label className="text-gray-500 text-[11px] block mb-1">Prefijo del código</label>
                      <Input placeholder="Ej: BIENVENIDO" value={coupon.code_prefix || ''}
                        onChange={e => updCoupon('code_prefix', e.target.value)} data-testid="coupon-prefix-input" />
                      <p className="text-gray-600 text-[11px] mt-1">El código final será algo como <code>{(coupon.code_prefix || 'SERA').toUpperCase().replace(/[^A-Z0-9]/g, '') || 'SERA'}-K7F3QZ</code>.</p>
                    </div>
                    <div>
                      <label className="text-gray-500 text-[11px] block mb-1">Nombre interno del cupón (opcional)</label>
                      <Input placeholder="Ej: Cupón bienvenida Popup Home" value={coupon.name || ''}
                        onChange={e => updCoupon('name', e.target.value)} data-testid="coupon-name-input" />
                    </div>
                    <div>
                      <label className="text-gray-500 text-[11px] block mb-1">Descripción interna (opcional)</label>
                      <textarea placeholder="Nota visible solo en tu backoffice de PrestaShop/Shopify, no la ve el cliente"
                        value={coupon.description || ''} onChange={e => updCoupon('description', e.target.value)}
                        data-testid="coupon-description-input" rows={2}
                        className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500" />
                      <p className="text-gray-600 text-[11px] mt-1">Ambos son solo para tu organización interna — nunca se muestran al cliente. Si los dejas vacíos se usa un nombre/descripción genéricos de Sera.</p>
                    </div>
                    <p className="text-gray-600 text-[11px]">Cada código es de un solo uso y se crea con las condiciones de arriba. Necesitas tener PrestaShop o Shopify conectado en <strong>Conectores</strong>.</p>
                  </div>
                )}
              </Section>
              {coupon.mode === 'dynamic' && integration.provider === 'brevo' && integration.enable_double_optin && (
                <Section title="Momento de creación (con Doble Opt-in activo)">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-white text-sm">Generar el cupón antes de confirmar el email</span>
                      <p className="text-gray-600 text-[11px]">Si lo desactivas, el código NO se crea hasta que el lead confirme su email vía Brevo — verá una página de confirmación con su código en ese momento.</p>
                    </div>
                    <Switch checked={coupon.generate_before_optin_confirmation !== false}
                      onCheckedChange={v => updCoupon('generate_before_optin_confirmation', v)} data-testid="coupon-generate-before-optin-switch" />
                  </div>
                </Section>
              )}
              <Section title="Barra de cuenta atrás (urgencia)">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <span className="text-white text-sm">Mostrar barra fija con el código y cuenta atrás</span>
                    <p className="text-gray-600 text-[11px]">Aparece tras registrarse y sigue visible mientras navega por la web (aunque recargue o cambie de página), hasta que expire o la cierre. Funciona con cupón fijo o único.</p>
                  </div>
                  <Switch checked={!!coupon.countdown_enabled}
                    onCheckedChange={v => updCoupon('countdown_enabled', v)} data-testid="coupon-countdown-enabled-switch" />
                </div>
                {coupon.countdown_enabled && (
                  <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-800">
                    <div>
                      <label className="text-gray-500 text-[11px] block mb-1">Posición</label>
                      <select value={coupon.countdown_position || 'top'}
                        onChange={e => updCoupon('countdown_position', e.target.value)}
                        data-testid="coupon-countdown-position-select"
                        className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white">
                        <option value="top">Arriba (barra completa)</option>
                        <option value="bottom_left">Abajo izquierda (flotante)</option>
                        <option value="bottom_right">Abajo derecha (flotante)</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-gray-500 text-[11px] block mb-1">Duración (minutos)</label>
                      <Input type="number" min="1" placeholder="60" value={coupon.countdown_minutes ?? ''}
                        onChange={e => updCoupon('countdown_minutes', e.target.value === '' ? '' : Number(e.target.value))}
                        data-testid="coupon-countdown-minutes-input" />
                    </div>
                    <p className="text-gray-600 text-[11px] col-span-2">La cuenta atrás es independiente de la caducidad real del cupón — es solo un temporizador de urgencia que empieza al registrarse.</p>
                  </div>
                )}
              </Section>
            </>
          )}

          {tab === 'Campos' && (
            <>
            <Section title="Qué datos pedir">
              <div className="space-y-3">
                {FIELD_DEFS.map(f => (
                  <div key={f.key} className="flex items-center justify-between" data-testid={`field-row-${f.key}`}>
                    <div className="flex items-center gap-2">
                      <Switch disabled={f.fixed} checked={!!fieldsMap[f.key]?.included}
                        onCheckedChange={v => updField(f.key, 'included', v)} data-testid={`field-include-${f.key}`} />
                      <span className="text-white text-sm">{f.label}{f.fixed && <span className="text-gray-500 text-xs"> (siempre incluido)</span>}</span>
                    </div>
                    {fieldsMap[f.key]?.included && (
                      <label className="flex items-center gap-1.5 text-xs text-gray-400">
                        <input type="checkbox" disabled={f.fixed} checked={!!fieldsMap[f.key]?.required}
                          onChange={e => updField(f.key, 'required', e.target.checked)} data-testid={`field-required-${f.key}`} />
                        Obligatorio
                      </label>
                    )}
                  </div>
                ))}
                {fieldsMap.consent?.included && (
                  <div className="space-y-2">
                    <Input placeholder="Texto legal de consentimiento" value={fieldsMap.consent.label}
                      onChange={e => updField('consent', 'label', e.target.value)} data-testid="consent-text-input" />
                    <div>
                      <Input placeholder="URL de la política de privacidad (opcional)" value={fieldsMap.consent.policy_url || ''}
                        onChange={e => updField('consent', 'policy_url', e.target.value)} data-testid="consent-policy-url-input" />
                      <p className="text-gray-600 text-[11px] mt-1">Si la rellenas, se mostrará un enlace "Política de privacidad" junto al checkbox en el popup.</p>
                    </div>
                  </div>
                )}
              </div>
            </Section>
            </>
          )}

          {tab === 'Disparadores' && (
            <>
              <Section title="Al cargar la página">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white text-sm">Mostrar tras un retraso</span>
                  <Switch checked={!!triggers.on_load?.enabled} onCheckedChange={v => updTrigger('on_load', 'enabled', v)} data-testid="trigger-onload-switch" />
                </div>
                {triggers.on_load?.enabled && (
                  <div className="flex items-center gap-2">
                    <input type="number" min="0" value={triggers.on_load?.delay_seconds ?? 5}
                      onChange={e => updTrigger('on_load', 'delay_seconds', parseInt(e.target.value) || 0)}
                      data-testid="trigger-onload-delay-input"
                      className="w-20 bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white" />
                    <span className="text-gray-500 text-xs">segundos</span>
                  </div>
                )}
              </Section>
              <Section title="Al intentar salir (exit-intent)">
                <div className="flex items-center justify-between">
                  <span className="text-white text-sm">Activar</span>
                  <Switch checked={!!triggers.exit_intent?.enabled} onCheckedChange={v => updTrigger('exit_intent', 'enabled', v)} data-testid="trigger-exitintent-switch" />
                </div>
              </Section>
              <Section title="Tras añadir un producto al carrito">
                <div className="flex items-center justify-between">
                  <span className="text-white text-sm">Activar</span>
                  <Switch checked={!!triggers.after_add_to_cart?.enabled} onCheckedChange={v => updTrigger('after_add_to_cart', 'enabled', v)} data-testid="trigger-cart-switch" />
                </div>
              </Section>
              <Section title="Por inactividad">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white text-sm">Activar</span>
                  <Switch checked={!!triggers.inactivity?.enabled} onCheckedChange={v => updTrigger('inactivity', 'enabled', v)} data-testid="trigger-inactivity-switch" />
                </div>
                {triggers.inactivity?.enabled && (
                  <div className="flex items-center gap-2">
                    <input type="number" min="5" value={triggers.inactivity?.seconds ?? 30}
                      onChange={e => updTrigger('inactivity', 'seconds', parseInt(e.target.value) || 5)}
                      data-testid="trigger-inactivity-seconds-input"
                      className="w-20 bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white" />
                    <span className="text-gray-500 text-xs">segundos sin actividad</span>
                  </div>
                )}
              </Section>
            </>
          )}

          {tab === 'Segmentación' && (
            <>
              <Section title="Mostrar a">
                <select value={targeting.customer_type || 'all'} onChange={e => setTargeting({ customer_type: e.target.value })}
                  data-testid="targeting-customer-type-select"
                  className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white">
                  <option value="all">Todos los visitantes</option>
                  <option value="new_only">Solo clientes nuevos (que no han comprado antes)</option>
                  <option value="returning_only">Solo clientes que ya han comprado</option>
                </select>
              </Section>
              <Section title="Frecuencia">
                <div className="flex items-center gap-2">
                  <span className="text-white text-sm">Si lo cierra, no volver a mostrar durante</span>
                  <input type="number" min="0" value={frequency.dont_show_days ?? 7}
                    onChange={e => setFrequency({ dont_show_days: parseInt(e.target.value) || 0 })}
                    data-testid="frequency-days-input"
                    className="w-16 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-sm text-white" />
                  <span className="text-gray-500 text-xs">días</span>
                </div>
                <p className="text-gray-600 text-[11px] mt-2">Si el visitante se suscribe, no se le volverá a mostrar nunca.</p>
              </Section>
            </>
          )}

          {tab === 'Traducciones' && (
            <>
              <Section title="Idioma de la tienda">
                <p className="text-gray-500 text-xs mb-3">
                  El contenido base (pestañas Diseño/Campos) se muestra por defecto. Si el visitante entra por el dominio de una tienda/idioma con traducción rellenada aquí, verá esa versión automáticamente — funciona con el multistore/multilenguaje de PrestaShop ya conectado.
                </p>
                <div className="flex gap-1.5 flex-wrap">
                  {detectedLanguages.map(l => (
                    <button key={l} onClick={() => setTransLang(l)} data-testid={`translation-lang-${l}`}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                        transLang === l ? 'bg-indigo-600 border-indigo-500 text-white' : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-white'
                      }`}>
                      <Globe size={11} className="inline mr-1 -mt-0.5" />{LANGUAGE_NAMES[l] || l.toUpperCase()}
                    </button>
                  ))}
                </div>
              </Section>
              {transLang && (
                <>
                  <Section title={`Contenido en ${LANGUAGE_NAMES[transLang] || transLang.toUpperCase()}`}>
                    <div className="space-y-2">
                      <Input placeholder={design.headline || 'Titular'} value={translations[transLang]?.headline || ''}
                        onChange={e => updTranslation(transLang, 'headline', e.target.value)} data-testid={`translation-headline-${transLang}`} />
                      <textarea placeholder={design.body_text || 'Texto'} value={translations[transLang]?.body_text || ''}
                        onChange={e => updTranslation(transLang, 'body_text', e.target.value)}
                        data-testid={`translation-body-${transLang}`} rows={2}
                        className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500" />
                      <Input placeholder={design.button_text || 'Texto del botón'} value={translations[transLang]?.button_text || ''}
                        onChange={e => updTranslation(transLang, 'button_text', e.target.value)} data-testid={`translation-button-${transLang}`} />
                      <textarea placeholder={design.success_message || 'Mensaje de éxito'} value={translations[transLang]?.success_message || ''}
                        onChange={e => updTranslation(transLang, 'success_message', e.target.value)}
                        data-testid={`translation-success-${transLang}`} rows={2}
                        className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500" />
                    </div>
                    <p className="text-gray-600 text-[11px] mt-2">Deja un campo vacío para usar el texto base (idioma por defecto) en ese idioma.</p>
                  </Section>
                  <Section title="Campos del formulario">
                    <div className="space-y-2">
                      {FIELD_DEFS.filter(f => f.key !== 'consent' && fieldsMap[f.key]?.included).map(f => (
                        <Input key={f.key} placeholder={`${fieldsMap[f.key].label} (${f.label})`}
                          value={translations[transLang]?.fields?.[f.key]?.label || ''}
                          onChange={e => updTranslationField(transLang, f.key, 'label', e.target.value)}
                          data-testid={`translation-field-${f.key}-${transLang}`} />
                      ))}
                      {fieldsMap.consent?.included && (
                        <>
                          <Input placeholder={fieldsMap.consent.label || 'Texto legal de consentimiento'}
                            value={translations[transLang]?.fields?.consent?.label || ''}
                            onChange={e => updTranslationField(transLang, 'consent', 'label', e.target.value)}
                            data-testid={`translation-field-consent-${transLang}`} />
                          <Input placeholder="URL de política de privacidad en este idioma (opcional)"
                            value={translations[transLang]?.fields?.consent?.policy_url || ''}
                            onChange={e => updTranslationField(transLang, 'consent', 'policy_url', e.target.value)}
                            data-testid={`translation-field-consent-policy-${transLang}`} />
                        </>
                      )}
                    </div>
                  </Section>
                </>
              )}
            </>
          )}

          {tab === 'Integración' && (
            <Section title="Enviar leads a">
              <select value={integration.provider || 'none'}
                onChange={e => setIntegration({ provider: e.target.value, list_id: null, list_name: null })}
                data-testid="integration-provider-select"
                className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white mb-3">
                <option value="none">Sin integración (solo guardar en Sera)</option>
                <option value="klaviyo" disabled={!integrations?.klaviyo?.connected}>Klaviyo {!integrations?.klaviyo?.connected && '(conecta en Conectores)'}</option>
                <option value="brevo" disabled={!integrations?.brevo?.connected}>Brevo {!integrations?.brevo?.connected && '(conecta en Conectores)'}</option>
              </select>
              {integration.provider !== 'none' && (
                <select value={integration.list_id || ''}
                  onChange={e => {
                    const list = providerLists.find(l => l.id === e.target.value);
                    setIntegration(prev => ({ ...prev, list_id: e.target.value, list_name: list?.name }));
                  }}
                  data-testid="integration-list-select"
                  className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white">
                  <option value="">Selecciona una lista…</option>
                  {providerLists.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              )}
            </Section>
          )}

          {tab === 'Integración' && integration.provider === 'brevo' && (
            <Section title="Doble Opt-in (Brevo)">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <span className="text-white text-sm">Activar Doble Opt-in nativo de Brevo</span>
                  <p className="text-gray-600 text-[11px]">Brevo enviará un email de confirmación; el contacto solo se añade a la lista cuando hace clic en el enlace. Mejora el cumplimiento RGPD.</p>
                </div>
                <Switch checked={!!integration.enable_double_optin}
                  onCheckedChange={v => setIntegration(prev => ({ ...prev, enable_double_optin: v }))}
                  data-testid="integration-doi-switch" />
              </div>
              {integration.enable_double_optin && (
                <div className="space-y-2">
                  <div>
                    <label className="text-gray-400 text-xs block mb-1">ID de plantilla de Doble Opt-in en Brevo</label>
                    <Input placeholder="Ej: 12" value={integration.doi_template_id || ''}
                      onChange={e => setIntegration(prev => ({ ...prev, doi_template_id: e.target.value }))}
                      data-testid="integration-doi-template-id-input" />
                    <p className="text-gray-600 text-[11px] mt-1">Crea una plantilla de email en Brevo marcada como "Doble confirmación" y usa <code>{'{{ params.DOIurl }}'}</code> en el enlace del botón. Copia aquí su ID.</p>
                  </div>
                  <div>
                    <label className="text-gray-400 text-xs block mb-1">URL de redirección tras confirmar (opcional)</label>
                    <Input placeholder="https://tu-tienda.com/gracias" value={integration.redirection_url || ''}
                      onChange={e => setIntegration(prev => ({ ...prev, redirection_url: e.target.value }))}
                      data-testid="integration-doi-redirect-input" />
                    <p className="text-gray-600 text-[11px] mt-1">Si se deja vacío, se usará la página donde el visitante vio el popup.</p>
                  </div>
                </div>
              )}
            </Section>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-6 pt-0 sticky bottom-0 bg-gray-900">
          <Button variant="outline" onClick={attemptClose} data-testid="popup-editor-cancel-btn"
            className="bg-transparent border-gray-700 text-gray-300 hover:bg-gray-800">
            Cancelar
          </Button>
          <Button onClick={() => save()} disabled={saving} data-testid="popup-editor-save-btn"
            className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2">
            {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
            Guardar
          </Button>
        </div>
      </div>
    </div>

    <AlertDialog open={showDiscardConfirm} onOpenChange={setShowDiscardConfirm}>
      <AlertDialogContent className="bg-gray-900 border-gray-700 text-white" data-testid="popup-discard-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-white">¿Descartar cambios sin guardar?</AlertDialogTitle>
          <AlertDialogDescription className="text-gray-400">
            Has hecho cambios en este popup que todavía no se han guardado. Si cierras ahora, se perderán.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="popup-discard-cancel-btn"
            className="bg-transparent border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white">
            Seguir editando
          </AlertDialogCancel>
          <AlertDialogAction onClick={onClose} data-testid="popup-discard-confirm-btn"
            className="bg-red-600 hover:bg-red-700 text-white">
            Descartar cambios
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
};
